import helpers from '../helpers';
import { Instruction } from '../machinelogic/types';
/**
 * converts a camelCased string to a title string
 *
 * example: camelCaseToText('helloWorld') => 'Hello World'
 */
export function camelCaseToText(string: string) {
  return string.replace(/([A-Z])/g, ' $1').replace(/^./, function (str) { return str.toUpperCase(); });
}

interface InstructionLabelsInterface {
  [instructionCategory: string]: {
    icon: string;
    label: {
      [instructionName: string]: string;
    }
  }
}

/**
 * used to retrieve icons and labels for instructions based on their instruction category and instruction name
 *
 * gets used by the getInstructionDisplay function
 */
const InstructionLabels: InstructionLabelsInterface = {
  waitFor: {
    icon: 'icon-time',
    label: {
      amountOfTime: 'Wait Time',
      event: 'Wait Event',
      motionCompletion: 'Wait Motion Complete',
      digitalInput: 'Wait Input',
      digitalInputEdge: 'Wait Input Transition',
    }
  },
  motor: {
    icon: 'icon-motor',
    label: {
      moveRelative: 'Move Relative',
      moveAbsolute: 'Move To Position',
      moveRelativeCombined: 'Move Rel. Combined',
      moveAbsoluteCombined: 'Move To Pos. Combined',
      home: 'Move To Home',
      push: 'Push Pneumatic',
      pull: 'Pull Pneumatic',
      idle: 'Idle Pneumatic',
      open: 'Open Bag Gripper',
      close: 'Close Bag Gripper',
      setSpeed: 'Set System Speed',
      setActuatorSpeed: 'Set Actuator Speed',
      setAcceleration: 'Set System Acceleration',
      setActuatorAcceleration: 'Set Actuator Acceleration',
      stopAllMotion: 'Stop All Motion',
      stopActuatorMotion: 'Stop Actuator Motion',
      setContinuousMove: 'Start Continuous Move',
      stopContinuousMove: 'Start Continuous Move',
      moveVFD: 'Start Continuous Move',
      stopVFD: 'Stop Continuous Move',
      defineAngle: 'Set Angle',
      moveToClosestAngle: 'Move To Closest Angle',
    },
  },
  trigger: {
    icon: 'icon-trigger',
    label:  {
      execute: 'Execute In Series',
      call: 'Execute In Parallel',
      terminate: 'Terminate',
      pause: 'Pause',
    },
  },
  fsm: {
    icon: 'icon-state-machine',
    label: {
      fsm: 'State Machine',
    }
  },
  output: {
    icon: 'icon-arrow-from-left',
    label: {
      digitalOutput: 'Digital Output',
      generateEvent: 'Output Event',
    },
  },
  setVariable: {
    icon: 'icon-variable',
    label: {
      fromDevice: 'Set Variable: Device',
      fromExpression: 'Set Variable: Expression',
      fromExpressionArray: 'Set Variables: Expression',
      fromActuatorPosition: 'Set Variable: Actuator'
    },
  },
  loop: {
    icon: 'icon-loop',
    label: {
      forever: 'Loop Forever',
      count: 'Loop Count',
      while: 'Loop While'
    },
  },
  condition: {
    icon: 'icon-condition-new',
    label: {
      if: 'If Condition'
    },
  },
  robot: {
    icon: 'icon-condition-new',
    label: {
      simulate: 'Robot Path',
    }
  },
  robotLoop: {
    icon: 'icon-loop',
    label: {
      array: 'Robot Loop',
    }
  },
  log: {
    icon: 'icon-start-chat flip-y',
    label: {
      console: 'Message to console',
      url: 'Message to URL'
    }
  }
};

interface InstructionDisplayReturnValue {
  icon?: string;
  name: string;
}

/**
 * Returns the icon and name for an instruction
 */
export const getInstructionDisplay = (instruction: Instruction):InstructionDisplayReturnValue => {
  const instructionCategory = instruction.category;
  const instructionName = instruction.name;

  const icon = InstructionLabels[instructionCategory]?.icon;
  let name = InstructionLabels[instructionCategory]?.label[instructionName];

  if(name === undefined) {
    console.error('Could not find name for instruction: ', instruction);
    name = camelCaseToText(instructionName);
  }

  return { icon, name };
};
